import React, { useState, useEffect, useCallback } from 'react'
import {
  Table,
  Input,
  Button,
  Space,
  Card,
  Collapse,
  DatePicker,
  Select,
  Checkbox,
  Row,
  Col,
  message,
  Empty,
  Spin
} from 'antd'
import {
  SearchOutlined,
  ExportOutlined,
  PrinterOutlined,
  DownOutlined,
  UpOutlined,
  WalletOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import {
  MobileRecordList,
  MobileRecordCard,
  MobileCardPagination
} from '../components/common/MobileRecordCard'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'
import dayjs from 'dayjs'
import { useMediaQuery } from 'react-responsive'

const { RangePicker } = DatePicker
const { Panel } = Collapse
const { Option } = Select

const PageContainer = styled.div`
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const ActionButtons = styled(Space)`
  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
  }
`

const StyledButton = styled(Button)`
  border-radius: 8px;
  font-weight: 500;
  height: 40px;
  padding: 0 20px;

  @media (max-width: 768px) {
    height: 36px;
    padding: 0 16px;
    font-size: 14px;
  }
`

const PrimaryButton = styled(StyledButton)`
  background: #7c3aed;
  border-color: #7c3aed;
  color: #fff;

  &:hover {
    background: #6d28d9;
    border-color: #6d28d9;
  }
`

const SecondaryButton = styled(StyledButton)`
  background: #fff;
  border-color: #7c3aed;
  color: #7c3aed;

  &:hover {
    background: #f3f0ff;
    border-color: #6d28d9;
    color: #6d28d9;
  }
`

const SearchCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
  border: none;

  .ant-card-body {
    padding: 24px;

    @media (max-width: 768px) {
      padding: 16px;
    }
  }

  .ant-collapse {
    background: transparent;
    border: none;

    .ant-collapse-item {
      border: none;

      .ant-collapse-header {
        padding: 0 !important;
        font-size: 16px;
        font-weight: 600;
        color: #1a1a1a;

        .ant-collapse-arrow {
          right: 0;
          padding: 0;
        }
      }

      .ant-collapse-content {
        border: none;
        background: transparent;
        padding-top: 16px;
      }
    }
  }
`

const SearchForm = styled.div`
  .search-row {
    margin-bottom: 16px;

    @media (max-width: 768px) {
      margin-bottom: 12px;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  .search-label {
    display: block;
    margin-bottom: 8px;
    color: #374151;
    font-weight: 500;
    font-size: 14px;
  }

  .ant-picker,
  .ant-input,
  .ant-select {
    width: 100%;
    border-radius: 8px;
    border: 1px solid #e5e7eb;

    &:hover,
    &:focus {
      border-color: #7c3aed;
    }

    &.ant-picker-focused,
    &.ant-input-focused,
    &.ant-select-focused .ant-select-selector {
      border-color: #7c3aed;
      box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.1);
    }
  }

  .ant-checkbox-wrapper {
    margin-top: 8px;
    font-size: 14px;
  }
`

const SummaryCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
  border: none;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);

  .ant-card-body {
    padding: 24px;

    @media (max-width: 768px) {
      padding: 16px;
    }
  }

  .summary-title {
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .summary-value {
    color: #fff;
    font-size: 32px;
    font-weight: 700;

    @media (max-width: 768px) {
      font-size: 24px;
    }
  }
`

const TableCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: none;

  .ant-card-body {
    padding: 0;
  }

  .ant-table {
    .ant-table-thead > tr > th {
      background: #f9fafb;
      border-bottom: 2px solid #e5e7eb;
      font-weight: 600;
      color: #374151;
      padding: 16px;
      text-align: center;

      @media (max-width: 768px) {
        padding: 12px 8px;
        font-size: 12px;
      }
    }

    .ant-table-tbody > tr > td {
      padding: 16px;
      text-align: center;
      border-bottom: 1px solid #f3f4f6;

      @media (max-width: 768px) {
        padding: 12px 8px;
        font-size: 12px;
      }
    }

    .ant-table-tbody > tr:hover > td {
      background: #f9fafb;
    }
  }
`

// 卡片右上角的剩餘金額（綠色重點數字）
const CardBalance = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #10b981;
  white-space: nowrap;
`

const escapeRemainingBalanceCsvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v)
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

const mapSorterToApiOrder = (order) => {
  if (order === 'ascend') return 'asc'
  if (order === 'descend') return 'desc'
  return 'desc'
}

const ValueAddedRemainingBalancePage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [loading, setLoading] = useState(false)
  const [exportCsvLoading, setExportCsvLoading] = useState(false)
  const [employees, setEmployees] = useState([])
  const [searchExpanded, setSearchExpanded] = useState(false)
  const [sortedInfo, setSortedInfo] = useState({})
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })

  // 搜索表单状态
  const [purchaseDateRange, setPurchaseDateRange] = useState(null)
  const [servicePersonnel, setServicePersonnel] = useState(null)
  const [serviceTimeRange, setServiceTimeRange] = useState(null)
  const [customer, setCustomer] = useState('')
  const [latestPurchaseTimeRange, setLatestPurchaseTimeRange] = useState(null)
  const [onlyStoreCustomers, setOnlyStoreCustomers] = useState(false)

  const [records, setRecords] = useState([])
  const [totalRemaining, setTotalRemaining] = useState(0)

  useEffect(() => {
    fetchEmployees()
    fetchRecords()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.current,
    pagination.pageSize,
    sortedInfo.field,
    sortedInfo.order
  ])

  const fetchEmployees = async () => {
    try {
      const response = await api.get(`/api/employees/${user.businessId}`)
      setEmployees(response.data || [])
    } catch (error) {
      console.error('獲取員工列表失敗:', error)
    }
  }

  const fetchRecords = async () => {
    setLoading(true)
    try {
      const response = await api.get(
        `/api/businesses/${user.businessId}/value-added-remaining-balance`,
        {
          params: {
            page: pagination.current,
            pageSize: pagination.pageSize,
            purchaseStartDate: purchaseDateRange?.[0]?.format('YYYY-MM-DD'),
            purchaseEndDate: purchaseDateRange?.[1]?.format('YYYY-MM-DD'),
            servicePersonnel: servicePersonnel,
            serviceStartTime: serviceTimeRange?.[0]?.format('YYYY-MM-DD HH:mm'),
            serviceEndTime: serviceTimeRange?.[1]?.format('YYYY-MM-DD HH:mm'),
            customer: customer,
            latestPurchaseStartTime:
              latestPurchaseTimeRange?.[0]?.format('YYYY-MM-DD HH:mm'),
            latestPurchaseEndTime:
              latestPurchaseTimeRange?.[1]?.format('YYYY-MM-DD HH:mm'),
            onlyStoreCustomers: onlyStoreCustomers,
            sortField: sortedInfo.field,
            sortOrder: mapSorterToApiOrder(sortedInfo.order)
          }
        }
      )

      // 處理新的 API 響應格式
      const data = response.data
      // 如果 success 為 false 或不存在 records，則視為錯誤
      if (data.success === false) {
        message.error(data.message || '獲取剩餘儲值金記錄失敗')
        setRecords([])
        setTotalRemaining(0)
        setPagination({
          ...pagination,
          total: 0
        })
      } else {
        // 正常響應：支持 { success: true, records: [...] } 或 { records: [...] } 格式
        setRecords(data.records || [])
        setTotalRemaining(data.totalRemaining || 0)

        // 使用 API 返回的 pagination 信息，如果沒有則使用默認值
        if (data.pagination) {
          setPagination({
            current: data.pagination.page || pagination.current,
            pageSize: data.pagination.pageSize || pagination.pageSize,
            total: data.total || data.pagination.total || 0
          })
        } else {
          setPagination({
            ...pagination,
            total: data.total || 0
          })
        }
      }
    } catch (error) {
      console.error('獲取剩餘儲值金記錄失敗:', error)
      message.error('獲取剩餘儲值金記錄失敗，請稍後再試')
      setRecords([])
      setTotalRemaining(0)
      setPagination({
        ...pagination,
        total: 0
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 })
    fetchRecords()
  }

  const buildRemainingBalanceQueryParams = useCallback(
    (pageNum, pageSizeNum) => ({
      page: pageNum,
      pageSize: pageSizeNum,
      purchaseStartDate: purchaseDateRange?.[0]?.format('YYYY-MM-DD'),
      purchaseEndDate: purchaseDateRange?.[1]?.format('YYYY-MM-DD'),
      servicePersonnel: servicePersonnel,
      serviceStartTime: serviceTimeRange?.[0]?.format('YYYY-MM-DD HH:mm'),
      serviceEndTime: serviceTimeRange?.[1]?.format('YYYY-MM-DD HH:mm'),
      customer: customer,
      latestPurchaseStartTime:
        latestPurchaseTimeRange?.[0]?.format('YYYY-MM-DD HH:mm'),
      latestPurchaseEndTime:
        latestPurchaseTimeRange?.[1]?.format('YYYY-MM-DD HH:mm'),
      onlyStoreCustomers: onlyStoreCustomers,
      sortField: sortedInfo.field || 'lastModifiedTime',
      sortOrder: mapSorterToApiOrder(sortedInfo.order)
    }),
    [
      purchaseDateRange,
      servicePersonnel,
      serviceTimeRange,
      customer,
      latestPurchaseTimeRange,
      onlyStoreCustomers,
      sortedInfo.field,
      sortedInfo.order
    ]
  )

  const resolveServicePersonnelName = useCallback(
    (record) => {
      const sp = record.servicePersonnel
      if (sp && typeof sp === 'object' && sp.name) return sp.name
      if (!sp) return ''
      const id = typeof sp === 'object' && sp._id ? sp._id : sp
      const emp = employees.find(
        (e) =>
          e._id === id ||
          (e._id && id && String(e._id) === String(id))
      )
      return emp?.name || String(sp)
    },
    [employees]
  )

  /** 依目前搜尋條件匯出剩餘儲值金 CSV（UTF-8 BOM） */
  const handleExportRemainingBalanceCsv = useCallback(async () => {
    if (!user?.businessId) return
    const EXPORT_PAGE = 100
    const MAX_PAGES = 500
    setExportCsvLoading(true)
    try {
      let pageNum = 1
      let totalPages = 1
      const all = []

      while (pageNum <= totalPages && pageNum <= MAX_PAGES) {
        const response = await api.get(
          `/api/businesses/${user.businessId}/value-added-remaining-balance`,
          { params: buildRemainingBalanceQueryParams(pageNum, EXPORT_PAGE) }
        )
        const data = response.data || {}
        if (data.success === false) {
          message.error(data.message || '匯出失敗')
          return
        }
        const batch = Array.isArray(data.records) ? data.records : []
        all.push(...batch)
        const tp = data.pagination?.totalPages
        const total = data.total
        if (tp != null && Number(tp) >= 1) {
          totalPages = Number(tp)
        } else if (total != null) {
          totalPages = Math.max(1, Math.ceil(Number(total) / EXPORT_PAGE))
        } else {
          totalPages = batch.length < EXPORT_PAGE ? pageNum : pageNum + 1
        }
        if (!batch.length) break
        pageNum++
      }

      if (pageNum > MAX_PAGES) {
        message.warning('已達匯出頁數上限，請縮小搜尋條件後再試')
      }

      const header = [
        '客戶姓名',
        '電話',
        '主要服務人員',
        '剩餘儲值金額',
        '最新服務時間',
        '最新儲值購買時間',
        '服務項目'
      ]
      const rows = [header.map(escapeRemainingBalanceCsvCell)]
      for (const rec of all) {
        const items = rec.serviceItems
        const itemsStr = !items
          ? ''
          : typeof items === 'string'
            ? items
            : Array.isArray(items)
              ? items.join('、')
              : ''
        rows.push(
          [
            rec.customerName || '',
            rec.customerPhone || '',
            resolveServicePersonnelName(rec),
            Number(rec.remainingBalance) || 0,
            rec.latestServiceTime
              ? dayjs(rec.latestServiceTime).format('YYYY-MM-DD')
              : '',
            rec.latestPurchaseTime
              ? dayjs(rec.latestPurchaseTime).format('YYYY-MM-DD HH:mm')
              : '',
            itemsStr
          ].map(escapeRemainingBalanceCsvCell)
        )
      }

      const csvContent =
        '\uFEFF' +
        rows.map((r) => r.join(',')).join('\r\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `客戶儲值餘額_${dayjs().format('YYYYMMDDHHmm')}.csv`
      a.click()
      URL.revokeObjectURL(url)
      message.success(`已匯出 ${all.length} 筆`)
    } catch (err) {
      console.error('匯出 CSV 失敗', err)
      message.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          '匯出失敗，請稍後再試'
      )
    } finally {
      setExportCsvLoading(false)
    }
  }, [
    user?.businessId,
    buildRemainingBalanceQueryParams,
    resolveServicePersonnelName
  ])

  const handleTableChange = (paginationConfig, filters, sorter) => {
    setSortedInfo({
      field: sorter.field,
      order: sorter.order
    })
    setPagination({
      ...pagination,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    })
  }

  const columns = [
    {
      title: '項次',
      dataIndex: 'index',
      key: 'index',
      width: 80,
      render: (_, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1
    },
    {
      title: '客戶',
      dataIndex: 'customerName',
      key: 'customerName',
      sorter: true,
      sortOrder: sortedInfo.field === 'customerName' && sortedInfo.order,
      render: (text) => text || '-'
    },
    {
      title: '電話號碼',
      dataIndex: 'customerPhone',
      key: 'customerPhone',
      sorter: true,
      sortOrder: sortedInfo.field === 'customerPhone' && sortedInfo.order,
      render: (text) => text || '-'
    },
    {
      title: '主要服務人員',
      dataIndex: 'servicePersonnel',
      key: 'servicePersonnel',
      render: (text) => text || '-'
    },
    {
      title: '剩餘儲值金額',
      dataIndex: 'remainingBalance',
      key: 'remainingBalance',
      sorter: true,
      sortOrder: sortedInfo.field === 'remainingBalance' && sortedInfo.order,
      align: 'right',
      render: (amount) => (amount ? `NT$ ${amount.toLocaleString()}` : 'NT$ 0')
    },
    {
      title: '最新服務時間(使用儲值金)',
      dataIndex: 'latestServiceTime',
      key: 'latestServiceTime',
      render: (date) => (date ? dayjs(date).format('YYYY-MM-DD') : '-')
    },
    {
      title: '最新儲值購買時間',
      dataIndex: 'latestPurchaseTime',
      key: 'latestPurchaseTime',
      render: (date) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-')
    },
    {
      title: '服務項目',
      dataIndex: 'serviceItems',
      key: 'serviceItems',
      render: (items) => {
        if (!items || items.length === 0) return '-'
        if (typeof items === 'string') return items
        return items.join('、')
      }
    },
    {
      title: '功能',
      key: 'action',
      width: 120,
      render: (_, record) => {
        // 優先使用 latestPurchaseRecordId，如果沒有則使用 customerId
        const recordId =
          record.latestPurchaseRecordId || record.recordId || record._id
        const queryParam = recordId
          ? `recordId=${recordId}`
          : `customerId=${record.customerId}`
        return (
          <Button
            type='link'
            size='small'
            icon={<WalletOutlined />}
            onClick={() => navigate(`/value-added/records?${queryParam}`)}
          >
            儲值記錄
          </Button>
        )
      }
    }
  ]

  // 手機版：表格轉卡片
  const renderMobileCards = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      )
    }
    if (!records || records.length === 0) {
      return (
        <div style={{ padding: 24 }}>
          <Empty description='暫無剩餘儲值金資料' />
        </div>
      )
    }
    return (
      <>
        <MobileRecordList $padded>
          {records.map((record, index) => {
            const seq =
              (pagination.current - 1) * pagination.pageSize + index + 1
            const recordId =
              record.latestPurchaseRecordId ||
              record.recordId ||
              record._id
            const queryParam = recordId
              ? `recordId=${recordId}`
              : `customerId=${record.customerId}`
            const serviceItems = (() => {
              const items = record.serviceItems
              if (!items || items.length === 0) return '-'
              if (typeof items === 'string') return items
              return items.join('、')
            })()
            return (
              <MobileRecordCard
                key={record._id}
                title={record.customerName || '-'}
                subtitle={`#${seq}${
                  record.customerPhone ? `　${record.customerPhone}` : ''
                }`}
                extra={
                  <CardBalance>
                    NT${' '}
                    {record.remainingBalance
                      ? Number(record.remainingBalance).toLocaleString()
                      : '0'}
                  </CardBalance>
                }
                rows={[
                  {
                    label: '主要服務人員',
                    value: record.servicePersonnel || '-'
                  },
                  {
                    label: '最新使用儲值',
                    value: record.latestServiceTime
                      ? dayjs(record.latestServiceTime).format('YYYY-MM-DD')
                      : '-'
                  },
                  {
                    label: '最新儲值購買',
                    value: record.latestPurchaseTime
                      ? dayjs(record.latestPurchaseTime).format(
                          'YYYY-MM-DD HH:mm'
                        )
                      : '-'
                  },
                  { label: '服務項目', value: serviceItems }
                ]}
                actions={
                  <Button
                    size='small'
                    icon={<WalletOutlined />}
                    onClick={() =>
                      navigate(`/value-added/records?${queryParam}`)
                    }
                  >
                    儲值記錄
                  </Button>
                }
              />
            )
          })}
        </MobileRecordList>
        <MobileCardPagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={(page, pageSize) =>
            handleTableChange({ current: page, pageSize }, {}, sortedInfo || {})
          }
        />
      </>
    )
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>剩餘儲值金查詢</PageTitle>
        <ActionButtons>
          <SecondaryButton
            icon={<ExportOutlined />}
            loading={exportCsvLoading}
            onClick={handleExportRemainingBalanceCsv}
          >
            匯出 CSV
          </SecondaryButton>
          <PrimaryButton onClick={() => navigate('/value-added/records')}>
            儲值記錄查詢
          </PrimaryButton>
        </ActionButtons>
      </PageHeader>

      <SearchCard>
        <Collapse
          activeKey={searchExpanded ? ['search'] : []}
          onChange={(keys) => setSearchExpanded(keys.includes('search'))}
          expandIcon={({ isActive }) =>
            isActive ? <UpOutlined /> : <DownOutlined />
          }
        >
          <Panel
            header={
              <span>
                <SearchOutlined style={{ marginRight: 8 }} />
                搜尋
              </span>
            }
            key='search'
          >
            <SearchForm>
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <div className='search-row'>
                    <label className='search-label'>購買日期區間</label>
                    <RangePicker
                      value={purchaseDateRange}
                      onChange={(dates) => setPurchaseDateRange(dates)}
                      format='YYYY-MM-DD'
                      style={{ width: '100%' }}
                      size='large'
                      allowClear
                      inputReadOnly={isMobile}
                      getPopupContainer={(trigger) => trigger.parentElement}
                    />
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <div className='search-row'>
                    <label className='search-label'>主要服務人員</label>
                    <Select
                      value={servicePersonnel}
                      onChange={(value) => setServicePersonnel(value)}
                      placeholder='請選擇服務人員'
                      size='large'
                      allowClear
                    >
                      {employees.map((emp) => (
                        <Option key={emp._id} value={emp._id}>
                          {emp.name}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Col>
              </Row>
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <div className='search-row'>
                    <label className='search-label'>服務時間(使用儲值金)</label>
                    <RangePicker
                      value={serviceTimeRange}
                      onChange={(dates) => setServiceTimeRange(dates)}
                      showTime
                      format='YYYY-MM-DD HH:mm'
                      style={{ width: '100%' }}
                      size='large'
                      allowClear
                      inputReadOnly={isMobile}
                      getPopupContainer={(trigger) => trigger.parentElement}
                    />
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <div className='search-row'>
                    <label className='search-label'>客戶</label>
                    <Input
                      value={customer}
                      onChange={(e) => setCustomer(e.target.value)}
                      placeholder='請輸入客戶姓名或電話'
                      size='large'
                      allowClear
                    />
                  </div>
                </Col>
              </Row>
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <div className='search-row'>
                    <label className='search-label'>最新儲值購買時間</label>
                    <RangePicker
                      value={latestPurchaseTimeRange}
                      onChange={(dates) => setLatestPurchaseTimeRange(dates)}
                      showTime
                      format='YYYY-MM-DD HH:mm'
                      style={{ width: '100%' }}
                      size='large'
                      allowClear
                      inputReadOnly={isMobile}
                      getPopupContainer={(trigger) => trigger.parentElement}
                    />
                  </div>
                </Col>
              </Row>
              <div style={{ textAlign: 'right', marginTop: 16 }}>
                <PrimaryButton icon={<SearchOutlined />} onClick={handleSearch}>
                  搜尋
                </PrimaryButton>
              </div>
            </SearchForm>
          </Panel>
        </Collapse>
      </SearchCard>

      <SummaryCard>
        <div className='summary-title'>剩餘儲值金額</div>
        <div className='summary-value'>
          NT$ {totalRemaining.toLocaleString()}
        </div>
      </SummaryCard>

      <TableCard>
        {isMobile ? (
          renderMobileCards()
        ) : (
        <Table
          columns={columns}
          dataSource={records}
          rowKey='_id'
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 筆記錄`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
        />
        )}
      </TableCard>
    </PageContainer>
  )
}

export default ValueAddedRemainingBalancePage
